type ShellNavListener = () => void

export type ShellNavVisit = {
  url: string | URL
  method: string
  only?: readonly string[]
  prefetch?: boolean
}

let pendingHref: string | null = null
let generation = 0
let armedGeneration = 0
let armedVisit: object | null = null
/** Page URL the accepted click left from. Any other settled URL means the visit landed. */
let originHref: string | null = null
const listeners = new Set<ShellNavListener>()

function emit() {
  for (const listener of listeners) listener()
}

/** Path + search, without hash, so menu predicates match `usePage().url`. */
export function shellNavTarget(href: string | URL): string {
  if (href instanceof URL) return href.pathname + href.search
  const hashless = href.split("#", 1)[0] ?? ""
  if (!hashless) return ""
  try {
    const url = new URL(hashless, "http://localhost")
    return url.pathname + url.search
  } catch {
    return hashless
  }
}

function isFullPageGet(visit: ShellNavVisit): boolean {
  if (visit.method.toLowerCase() !== "get") return false
  if (visit.prefetch) return false
  return (visit.only?.length ?? 0) === 0
}

export function getShellNavPendingHref(): string | null {
  return pendingHref
}

export function subscribeShellNavPending(listener: ShellNavListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function clearPending() {
  armedVisit = null
  originHref = null
  if (pendingHref == null) return
  pendingHref = null
  emit()
}

function armVisit(visit: ShellNavVisit, gen: number, from?: string) {
  if (generation !== gen || pendingHref == null) return
  if (!isFullPageGet(visit)) return
  if (shellNavTarget(visit.url) !== pendingHref) return
  armedGeneration = gen
  armedVisit = visit
  if (from != null) originHref = from
}

export type ShellNavClick = {
  defaultPrevented: boolean
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
  button: number
  currentTarget: EventTarget | null
}

/** Same guards as sidebar links. `Link` passes `MouseEvent<Element>`, which matches this shape. */
export function beginShellNavOnClick(event: ShellNavClick, href: string): void {
  if (event.defaultPrevented) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
  if (
    typeof HTMLAnchorElement !== "undefined" &&
    event.currentTarget instanceof HTMLAnchorElement &&
    event.currentTarget.target === "_blank"
  ) {
    return
  }
  beginShellNav(href)
}

/**
 * Optimistic menu click. Rolls back before paint when Inertia never accepts the visit
 * (modified click is filtered by the caller; same-page and prevented visits too).
 * The rollback is two microtasks deep so a `before` handler can arm a prefetched visit
 * (those do not fire `start`) before we decide the click was a no-op.
 */
export function beginShellNav(href: string | URL): void {
  const next = shellNavTarget(href)
  if (!next) return
  const gen = ++generation
  armedGeneration = 0
  armedVisit = null
  originHref = null
  if (pendingHref !== next) {
    pendingHref = next
    emit()
  }
  queueMicrotask(() => {
    queueMicrotask(() => {
      if (generation !== gen) return
      if (armedGeneration === gen) return
      clearPending()
    })
  })
}

/**
 * `router.on("before")`. Prefetch hovers are ignored. A cancelled visit does not arm.
 * `fromUrl` is the Inertia page URL at click time (not `window.location`, pages rewrite it via replaceState).
 */
export function acceptShellNavVisit(visit: ShellNavVisit, cancelled: () => boolean, fromUrl?: string): void {
  if (!isFullPageGet(visit) || pendingHref == null) return
  if (shellNavTarget(visit.url) !== pendingHref) return
  const gen = generation
  const from = fromUrl == null ? undefined : shellNavTarget(fromUrl)
  queueMicrotask(() => {
    if (cancelled()) return
    armVisit(visit, gen, from)
  })
}

/** `router.on("start")` for a real request. Cached prefetch clicks never get here. */
export function noteShellNavVisitStart(visit: ShellNavVisit): void {
  armVisit(visit, generation)
}

/**
 * The server may redirect a menu href: `/workspace/flow` → `?flow=1`, `/workspace/settings` → `/settings/general`,
 * or elsewhere on a denied section. A query-less click settles on its path or below it; otherwise any URL other
 * than the one the click left from is the landed visit (partial reloads of the old page keep that URL).
 */
function settledOnPending(pageUrl: string): boolean {
  if (pendingHref == null) return false
  const settled = shellNavTarget(pageUrl)
  if (settled === pendingHref) return true
  if (!pendingHref.includes("?")) {
    const path = settled.split("?", 1)[0] ?? ""
    const base = pendingHref.replace(/\/$/, "")
    if (path === pendingHref || (base && path.startsWith(`${base}/`))) return true
  }
  return originHref != null && settled !== originHref
}

/**
 * `router.on("success")` — clear in the same turn as the page swap when the URL matches.
 * A click served from the prefetch cache fires no `start`/`finish`, so this is its only end signal.
 */
export function noteShellNavPageSettled(pageUrl: string): void {
  if (pendingHref == null || armedGeneration !== generation) return
  if (!settledOnPending(pageUrl)) return
  clearPending()
}

/**
 * `router.on("finish")` covers cancel, network error, and redirects whose final URL
 * differs from the clicked href. Partials (`only`) and a superseded click do not clear.
 */
export function noteShellNavVisitEnd(visit: ShellNavVisit): void {
  if (pendingHref == null || !isFullPageGet(visit)) return
  if (armedGeneration !== generation) return
  if (visit !== armedVisit && shellNavTarget(visit.url) !== pendingHref) return
  clearPending()
}

export function resetShellNavPendingForTests(): void {
  generation += 1
  armedGeneration = 0
  armedVisit = null
  originHref = null
  pendingHref = null
}
